/**
 * BCP document loading hook for edit mode
 */

import { useState, useEffect, useCallback } from 'react';
import { bcpService } from '../services/bcpService';
import type { BCPDocument } from '../types/bcp';
import type { BCPFormData } from '../types';
import { logger } from '../utils/logger';

interface UseBCPLoaderOptions {
  bcpId: string | null;
  tenantId: string | null;
  onLoad?: (bcp: BCPDocument) => void;
  onError?: (error: string) => void;
}

export const useBCPLoader = ({ bcpId, tenantId, onLoad, onError }: UseBCPLoaderOptions) => {
  const [loading, setLoading] = useState(false);
  const [bcpDocument, setBcpDocument] = useState<BCPDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    logger.hookStart('useBCPLoader', { bcpId, tenantId });

    if (!bcpId || bcpId === 'new') {
      logger.debug('No bcpId or new mode, skipping BCP loading', {
        component: 'useBCPLoader',
        bcpId
      });
      setIsEditMode(false);
      setBcpDocument(null);
      return;
    }

    const loadBCP = async () => {
      logger.info('Starting BCP document load', {
        component: 'useBCPLoader',
        action: 'loadBCP',
        data: { bcpId, tenantId }
      });

      setLoading(true);
      setError(null);

      try {
        const bcp = await bcpService.getBCP(bcpId, tenantId || undefined);
        
        logger.info('BCP document loaded successfully', {
          component: 'useBCPLoader',
          action: 'loadBCP',
          data: {
            bcpId: bcp.id,
            title: bcp.content?.title,
            hasFormData: !!bcp.form_data,
            formDataKeys: bcp.form_data ? Object.keys(bcp.form_data) : [],
            formDataSample: bcp.form_data ? Object.keys(bcp.form_data).slice(0, 3).reduce((acc, key) => {
              acc[key] = bcp.form_data![key];
              return acc;
            }, {} as Record<string, any>) : {},
            hasSections: !!bcp.content?.sections,
            sectionKeys: bcp.content?.sections ? Object.keys(bcp.content.sections) : []
          }
        });

        setBcpDocument(bcp);
        setIsEditMode(true);
        onLoad?.(bcp);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'BCPの読み込みに失敗しました';
        
        logger.error('BCP loading failed', {
          component: 'useBCPLoader',
          action: 'loadBCP',
          error: err
        });

        setError(errorMessage);
        setIsEditMode(false);
        setBcpDocument(null);
        onError?.(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadBCP();
  }, [bcpId, tenantId]);

  /**
   * Convert BCP document content to form data format
   */
  const extractFormData = (bcp: BCPDocument): Partial<BCPFormData> => {
    logger.debug('Extracting form data', {
      component: 'useBCPLoader',
      action: 'extractFormData',
      data: {
        hasFormData: !!bcp.form_data,
        formDataKeys: bcp.form_data ? Object.keys(bcp.form_data).length : 0,
        hasSections: !!bcp.content?.sections,
        sectionKeys: bcp.content?.sections ? Object.keys(bcp.content.sections) : []
      }
    });

    // For Basic BCP, form_data is already merged with all sections
    // so we should prioritize it
    if (bcp.form_data && Object.keys(bcp.form_data).length > 0) {
      logger.debug('Using form_data', {
        component: 'useBCPLoader',
        action: 'extractFormData',
        data: {
          fieldCount: Object.keys(bcp.form_data).length,
          sampleFields: Object.keys(bcp.form_data).slice(0, 5)
        }
      });
      return bcp.form_data;
    }

    // Fallback: try to extract from content.sections
    if (bcp.content?.sections) {
      const sections = bcp.content.sections;
      const formData: Partial<BCPFormData> = {};

      // Map sections to form fields
      Object.keys(sections).forEach(key => {
        const value = sections[key];
        if (typeof value === 'string') {
          // Direct string mapping
          formData[key as keyof BCPFormData] = value;
        } else if (value && typeof value === 'object') {
          // For Basic BCP, section value is an object with field data
          // Merge all fields from the section object
          Object.assign(formData, value);
        }
      });

      logger.debug('Extracted from sections', {
        component: 'useBCPLoader',
        action: 'extractFormData',
        data: {
          fieldCount: Object.keys(formData).length,
          sampleFields: Object.keys(formData).slice(0, 5)
        }
      });

      return formData;
    }

    logger.warn('No form data found', {
      component: 'useBCPLoader',
      action: 'extractFormData'
    });

    return {};
  };

  /**
   * Get initial form data for editing
   */
  const getInitialFormData = useCallback((): Partial<BCPFormData> => {
    if (!bcpDocument) return {};
    return extractFormData(bcpDocument);
  }, [bcpDocument]);

  /**
   * Get section data for Basic BCP
   */
  const getSectionData = useCallback((sectionKey: string): Record<string, any> | null => {
    if (!bcpDocument || !bcpDocument.content?.sections) {
      return null;
    }
    return bcpDocument.content.sections[sectionKey] || null;
  }, [bcpDocument]);

  /**
   * Get progress data for Basic BCP
   */
  const getProgress = useCallback((): Record<string, any> | null => {
    if (!bcpDocument || !bcpDocument.content?.sections) {
      return null;
    }
    return bcpDocument.content.sections.progress || null;
  }, [bcpDocument]);

  /**
   * Reload BCP document (for example, after save to update status)
   */
  const reload = useCallback(async () => {
    if (!bcpId || bcpId === 'new') {
      return;
    }

    logger.info('Reloading BCP document', {
      component: 'useBCPLoader',
      action: 'reload',
      data: { bcpId, tenantId }
    });

    setLoading(true);
    setError(null);

    try {
      const bcp = await bcpService.getBCP(bcpId, tenantId || undefined);

      logger.info('BCP document reloaded successfully', {
        component: 'useBCPLoader',
        action: 'reload',
        data: {
          bcpId: bcp.id,
          status: bcp.status
        }
      });

      setBcpDocument(bcp);
      setIsEditMode(true);
      onLoad?.(bcp);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'BCPの読み込みに失敗しました';

      logger.error('BCP reload failed', {
        component: 'useBCPLoader',
        action: 'reload',
        error: err
      });

      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [bcpId, tenantId, onLoad, onError]);

  return {
    loading,
    bcpDocument,
    error,
    isEditMode,
    getInitialFormData,
    getSectionData,
    getProgress,
    reload,
  };
};